import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Switch } from "@volca/ui/components/ui/switch";
import { ArchiveTemplateDialog } from "@/profitability/template-page/archive-template-dialog";
import { ManageAccessDialog } from "@/profitability/template-page/manage-access/manage-access-dialog";
import { useViewerMutations } from "@/profitability/template-page/manage-access/query";
import { useTemplateViewers } from "@/profitability/template-page/manage-access/query";
import type { ProfitabilityReport } from "@/profitability/types";
import { SettingsGroup, SettingsRow } from "./settings-group";
import { SettingsLoadError } from "./settings-load-error";

/** "Dana Reyes and Sam Ito", "Dana Reyes, Sam Ito and 2 others". */
export const peopleLine = (names: string[]) => {
  if (names.length <= 2) return names.join(" and ");
  const rest = names.length - 2;
  return `${names.slice(0, 2).join(", ")} and ${rest} other${rest === 1 ? "" : "s"}`;
};

/**
 * Who sees the template besides owners and finance: the managers it is
 * shared with, who see its Review and records and hear when a period is
 * reviewed. Finance only.
 */
export const SharingGroup = ({
  businessId,
  report,
}: {
  businessId: string;
  report: ProfitabilityReport;
}) => {
  const [open, setOpen] = useState(false);
  const viewers = useTemplateViewers(businessId, report.id);
  const shared = viewers.data?.viewers ?? [];
  const { setEmail } = useViewerMutations(businessId, report.id);
  return (
    <>
      <SettingsGroup
        title="Access"
        description="Owners and finance always have access. Add managers who need to view reports and work on findings."
      >
        {viewers.isPending ? (
          <div className="p-4">
            <Skeleton
              role="status"
              aria-label="Loading access"
              className="h-10 w-full"
            />
          </div>
        ) : viewers.isError ? (
          <SettingsLoadError
            label="Access could not load."
            retry={() => void viewers.refetch()}
          />
        ) : (
          <SettingsRow
            label="Managers"
            value={
              shared.length
                ? peopleLine(shared.map((viewer) => viewer.user.full_name))
                : "No managers added"
            }
            action={
              <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
                Manage access
              </Button>
            }
          />
        )}
        <ManageAccessDialog
          businessId={businessId}
          report={report}
          open={open}
          onOpenChange={setOpen}
        />
      </SettingsGroup>
      {viewers.data && (
        <SettingsGroup
          title="Notifications"
          description="Choose who receives a summary when a period is marked reviewed. Assigned reviewers also receive their own findings."
        >
          {shared.length ? (
            shared.map((viewer) => (
              <SettingsRow
                key={viewer.id}
                label={viewer.user.full_name}
                hint="Email after review"
                action={
                  <Switch
                    checked={viewer.email_on_review}
                    disabled={setEmail.isPending}
                    aria-label={`Email ${viewer.user.full_name} after review`}
                    onCheckedChange={(email) =>
                      setEmail.mutate({ id: viewer.id, email })
                    }
                  />
                }
              />
            ))
          ) : (
            <SettingsRow
              label="No managers to notify"
              hint="Add a manager under Access to enable review emails."
            />
          )}
        </SettingsGroup>
      )}
    </>
  );
};

/** Putting the template away, last and on its own, like any admin's. */
export const ArchiveGroup = ({
  businessId,
  report,
}: {
  businessId: string;
  report: ProfitabilityReport;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <SettingsGroup title="Archive agent">
      <SettingsRow
        label={report.name}
        hint="Hide this agent from the active list. Past reports and findings stay available, and you can restore the agent later."
        action={
          <Button
            variant="outline"
            size="sm"
            disabled={!!report.archived_at}
            onClick={() => setOpen(true)}
          >
            {report.archived_at ? "Archived" : "Archive agent"}
          </Button>
        }
      />
      <ArchiveTemplateDialog
        businessId={businessId}
        report={report}
        open={open}
        onOpenChange={setOpen}
      />
    </SettingsGroup>
  );
};
